<template>
  <div class="gl-form-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-form-header">
        <div>
          <div class="gl-form-breadcrumb">
            <router-link to="/">Home</router-link>
            <span>/</span>
            <router-link to="/owner/categories">Categories</router-link>
            <span>/</span>
            <span>{{ isEdit ? 'Edit' : 'Create' }}</span>
          </div>
          <h1 class="gl-form-title">{{ isEdit ? 'Edit Category' : 'Create Category' }}</h1>
        </div>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Loading -->
      <div v-if="loading" class="gl-loading">
        <div class="spinner-border" role="status" style="color:#900C27;width:28px;height:28px;border-width:3px"></div>
        <span>Loading category…</span>
      </div>

      <div v-else class="gl-cat-layout">

        <!-- LEFT: Form -->
        <form @submit.prevent="submitForm" class="gl-form-body">

          <!-- Live preview strip -->
          <div class="gl-preview-strip" v-if="form.name">
            <div class="gl-preview-img">
              <img v-if="form.image" :src="form.image" class="gl-preview-img-real">
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
            </div>
            <div class="gl-preview-info">
              <div class="gl-preview-name">{{ form.name }}</div>
              <div class="gl-preview-meta">
                <span v-if="form.slug" class="gl-preview-slug">{{ form.slug }}</span>
                <span v-if="form.sort_order" class="gl-preview-order">#{{ form.sort_order }}</span>
              </div>
            </div>
            <div :class="['gl-preview-status', form.is_active ? 'active' : 'inactive']">
              <span class="gl-preview-dot"></span>{{ form.is_active ? 'Active' : 'Inactive' }}
            </div>
          </div>

          <!-- ── Section 1: Basic Info ── -->
          <div class="gl-section" style="animation-delay:0ms">
            <div class="gl-section-header">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
              </div>
              <div>
                <div class="gl-section-title">Basic Information</div>
                <div class="gl-section-desc">Name, slug, display order and description</div>
              </div>
            </div>

            <div class="gl-fields">
              <!-- Name + Sort Order -->
              <div class="gl-field-row">
                <div class="gl-field" style="flex:3">
                  <label class="gl-label">Category Name <span class="gl-req">*</span></label>
                  <input type="text" class="gl-input" :class="{ 'gl-input-err': errors.name }" v-model="form.name" placeholder="e.g. Main Course, Beverages" required>
                  <div v-if="errors.name" class="gl-err">{{ errors.name[0] }}</div>
                </div>
                <div class="gl-field" style="flex:1">
                  <label class="gl-label">Sort Order</label>
                  <input type="number" class="gl-input" v-model="form.sort_order" min="0" placeholder="0">
                  <div class="gl-hint">Lower = shown first</div>
                </div>
              </div>

              <!-- Slug -->
              <div class="gl-field">
                <label class="gl-label">Slug <span class="gl-optional">(optional)</span></label>
                <input type="text" class="gl-input" :class="{ 'gl-input-err': errors.slug }" v-model="form.slug" placeholder="auto-generated-from-name">
                <div class="gl-hint">Leave empty to auto-generate from name</div>
                <div v-if="errors.slug" class="gl-err">{{ errors.slug[0] }}</div>
              </div>

              <!-- Description -->
              <div class="gl-field">
                <label class="gl-label">Description <span class="gl-optional">(optional)</span></label>
                <textarea class="gl-input gl-textarea" :class="{ 'gl-input-err': errors.description }" v-model="form.description" rows="3" placeholder="e.g. Our signature main dishes made with fresh local ingredients"></textarea>
                <div v-if="errors.description" class="gl-err">{{ errors.description[0] }}</div>
              </div>
            </div>
          </div>

          <!-- ── Section 2: Parent Category ── -->
          <div class="gl-section" style="animation-delay:60ms">
            <div class="gl-section-header">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
              </div>
              <div>
                <div class="gl-section-title">Parent Category</div>
                <div class="gl-section-desc">Nest this under another category to create sub-categories</div>
              </div>
            </div>

            <div class="gl-fields">
              <div class="gl-field">
                <label class="gl-label">Parent Category <span class="gl-optional">(optional)</span></label>
                <select class="gl-input gl-select" :class="{ 'gl-input-err': errors.parent_id }" v-model="form.parent_id">
                  <option :value="null">None (Top Level)</option>
                  <option v-for="category in parentCategories" :key="category.id" :value="category.id">
                    {{ category.name }}
                  </option>
                </select>
                <div class="gl-hint">Example: "Hot Drinks" under "Beverages"</div>
                <div v-if="errors.parent_id" class="gl-err">{{ errors.parent_id[0] }}</div>
              </div>
            </div>
          </div>

          <!-- ── Section 3: Category Image ── -->
          <div class="gl-section" style="animation-delay:120ms">
            <div class="gl-section-header">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              </div>
              <div>
                <div class="gl-section-title">Category Image</div>
                <div class="gl-section-desc">Shown on the menu and category listing</div>
              </div>
            </div>

            <div class="gl-fields">
              <div class="gl-field">
                <div class="gl-img-upload" :class="{ 'gl-img-upload--has': form.image }" @click="$refs.categoryImageInput.click()">
                  <input ref="categoryImageInput" type="file" accept="image/jpeg,image/png,image/jpg,image/webp" style="display:none" @change="handleImageUpload">

                  <!-- Uploading -->
                  <div v-if="uploadingImage" class="gl-img-uploading">
                    <div class="spinner-border" style="width:22px;height:22px;border-width:2.5px;color:#900C27"></div>
                    <span>Uploading image…</span>
                  </div>

                  <!-- Has image -->
                  <div v-else-if="form.image && !uploadImgError" class="gl-img-preview">
                    <img :src="form.image" class="gl-img-thumb" @error="uploadImgError = true">
                    <div class="gl-img-overlay">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      <span>Replace image</span>
                    </div>
                  </div>

                  <!-- Empty -->
                  <div v-else class="gl-img-empty">
                    <div class="gl-img-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    </div>
                    <div class="gl-img-label">Click to upload image</div>
                    <div class="gl-img-sub">JPEG, PNG or WebP · max 4MB</div>
                  </div>
                </div>

                <div v-if="imageError" class="gl-err" style="margin-top:6px">{{ imageError }}</div>
                <div v-if="errors.image" class="gl-err" style="margin-top:6px">{{ errors.image[0] }}</div>
              </div>
            </div>
          </div>

          <!-- ── Section 4: Status ── -->
          <div class="gl-section" style="animation-delay:180ms">
            <div class="gl-section-header">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </div>
              <div>
                <div class="gl-section-title">Visibility</div>
                <div class="gl-section-desc">Control whether this category appears on the menu</div>
              </div>
            </div>

            <div class="gl-fields">
              <div class="gl-toggle-row">
                <div class="gl-toggle-wrap" @click="form.is_active = !form.is_active">
                  <div :class="['gl-toggle', { on: form.is_active }]">
                    <div class="gl-toggle-knob"></div>
                  </div>
                </div>
                <div class="gl-toggle-info">
                  <div class="gl-toggle-label">Active</div>
                  <div class="gl-toggle-desc">{{ form.is_active ? 'Visible to customers on the menu' : 'Hidden from customers — won\'t appear on menu' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Form Actions (desktop) ── -->
          <div class="gl-form-actions">
            <button type="submit" class="gl-submit-btn" :disabled="submitting">
              <span v-if="submitting" class="spinner-border spinner-border-sm" style="width:14px;height:14px;border-width:2px"></span>
              <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              {{ isEdit ? 'Update Category' : 'Create Category' }}
            </button>
            <router-link to="/owner/categories" class="gl-cancel-btn">Cancel</router-link>
          </div>

          <!-- ── Sticky bar (mobile only) ── -->
          <div class="gl-sticky-bar">
            <router-link to="/owner/categories" class="gl-cancel-btn gl-sticky-cancel">Cancel</router-link>
            <button type="submit" class="gl-submit-btn gl-sticky-submit" :disabled="submitting">
              <span v-if="submitting" class="spinner-border spinner-border-sm" style="width:14px;height:14px;border-width:2px"></span>
              <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              {{ isEdit ? 'Update Category' : 'Create Category' }}
            </button>
          </div>

        </form>

        <!-- RIGHT: Preview card -->
        <div class="gl-cat-preview-col">
          <div class="gl-cat-preview-card">
            <div class="gl-cat-preview-label">Category Preview</div>

            <!-- Image preview -->
            <div class="gl-cat-preview-img-wrap">
              <img v-if="form.image && !previewImgError" :src="form.image" class="gl-cat-preview-img" @error="previewImgError = true">
              <div v-else class="gl-cat-preview-img-placeholder">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                <span>No image</span>
              </div>
              <!-- Sort order badge -->
              <div v-if="form.sort_order" class="gl-cat-preview-order">#{{ form.sort_order }}</div>
            </div>

            <!-- Info -->
            <div class="gl-cat-preview-info">
              <div class="gl-cat-preview-name">{{ form.name || 'Category Name' }}</div>
              <div class="gl-cat-preview-slug">{{ form.slug || 'category-slug' }}</div>
              <div class="gl-cat-preview-meta">
                <span class="gl-cat-preview-count">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                  {{ productsCount }} {{ productsCount === 1 ? 'product' : 'products' }}
                </span>
                <span :class="['gl-cat-preview-badge', form.is_active ? 'active' : 'inactive']">
                  <span class="gl-cat-preview-dot"></span>
                  {{ form.is_active ? 'Active' : 'Inactive' }}
                </span>
              </div>
            </div>

            <!-- Action buttons mock -->
            <div class="gl-cat-preview-actions">
              <div class="gl-cat-preview-btn-edit">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Edit
              </div>
              <div class="gl-cat-preview-btn-del">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
              </div>
            </div>
          </div>

          <!-- Tips card -->
          <div class="gl-tips-card">
            <div class="gl-tips-title">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              Tips
            </div>
            <ul class="gl-tips-list">
              <li>Use clear, descriptive names like "Main Course" or "Cold Drinks"</li>
              <li>Sort order controls the display sequence — lower numbers appear first</li>
              <li>A category image helps customers browse the menu visually</li>
              <li>Sub-categories are great for large menus (e.g. "Iced" under "Coffee")</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ownerCategoryApi } from '@/services/ownerApi';

export default {
  name: 'CategoryForm',

  data() {
    return {
      loading: false,
      submitting: false,
      uploadingImage: false,
      uploadImgError: false,
      imageError: '',
      previewImgError: false,
      isEdit: false,
      errors: {},
      productsCount: 0,
      parentCategories: [],
      form: {
        name: '',
        slug: '',
        description: '',
        image: '',
        parent_id: null,
        sort_order: 0,
        is_active: true
      }
    };
  },

  watch: {
    'form.name'(val) {
      if (!this.isEdit && !this.form.slug) {
        this.form.slug = this.slugify(val);
      }
    },
    'form.image'() {
      this.previewImgError = false;
      this.uploadImgError = false;
    }
  },

  mounted() {
    this.fetchParentCategories();
    if (this.$route.params.id) {
      this.isEdit = true;
      this.fetchCategory();
    }
  },

  methods: {
    async handleImageUpload(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 4 * 1024 * 1024) {
        this.imageError = 'Image must be under 4MB.';
        return;
      }
      this.imageError = '';
      this.uploadingImage = true;
      try {
        const formData = new FormData();
        formData.append('image', file);
        const response = await ownerCategoryApi.uploadImage(formData);
        this.form.image = response.data.url;
      } catch (err) {
        this.imageError = err?.response?.data?.message ?? 'Upload failed. Please try again.';
      } finally {
        this.uploadingImage = false;
        e.target.value = '';
      }
    },

    slugify(text) {
      return text.toString().toLowerCase().trim()
        .replace(/\s+/g, '-').replace(/[^\w-]+/g, '').replace(/--+/g, '-');
    },

    async fetchParentCategories() {
      try {
        const response = await ownerCategoryApi.list({ per_page: 100 });
        this.parentCategories = response.data.data.filter(c =>
          !this.isEdit || c.id !== parseInt(this.$route.params.id)
        );
      } catch (error) {
        console.error('Failed to fetch parent categories:', error);
      }
    },

    async fetchCategory() {
      try {
        this.loading = true;
        const response = await ownerCategoryApi.get(this.$route.params.id);
        const category = response.data.data;
        this.form = {
          name: category.name,
          slug: category.slug,
          description: category.description || '',
          image: category.image || '',
          parent_id: category.parent_id,
          sort_order: category.sort_order,
          is_active: category.is_active
        };
        this.productsCount = category.products_count ?? 0;
      } catch (error) {
        console.error('Failed to fetch category:', error);
        this.$router.push('/owner/categories');
      } finally {
        this.loading = false;
      }
    },

    async submitForm() {
      try {
        this.submitting = true;
        this.errors = {};
        const data = { ...this.form };
        if (!data.slug) data.slug = this.slugify(data.name);
        if (this.isEdit) {
          await ownerCategoryApi.update(this.$route.params.id, data);
        } else {
          await ownerCategoryApi.create(data);
        }
        this.$router.push('/owner/categories');
      } catch (error) {
        console.error('Failed to save category:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          alert(error.response?.data?.message || 'Failed to save category');
        }
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
/* ── Page layout ── */
.gl-form-page { padding-bottom: 60px; }

.gl-form-header {
  display: flex; align-items: flex-end; justify-content: space-between;
  padding: 28px 0 20px;
}
.gl-form-breadcrumb {
  display: flex; align-items: center; gap: 6px;
  font-size: 12.5px; color: #9ca3af; margin-bottom: 4px;
}
.gl-form-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-form-breadcrumb a:hover { color: #900C27; }
.gl-form-breadcrumb span { color: #9ca3af; }
.gl-form-title { font-size: 24px; font-weight: 700; color: #111318; margin: 0; letter-spacing: -0.4px; }

/* ── Loading ── */
.gl-loading {
  display: flex; align-items: center; gap: 12px;
  padding: 60px 0; justify-content: center;
  font-size: 14px; color: #9ca3af;
}

/* ── Two-column layout ── */
.gl-cat-layout {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 24px;
  align-items: start;
}

/* ── Form body ── */
.gl-form-body { display: flex; flex-direction: column; gap: 16px; }

/* ── Preview strip ── */
.gl-preview-strip {
  display: flex; align-items: center; gap: 12px;
  background: #f0fdf4; border: 1.5px solid #bbf7d0;
  border-radius: 12px; padding: 12px 16px;
  animation: gl-fade-in 0.25s ease;
}
@keyframes gl-fade-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
.gl-preview-img {
  width: 40px; height: 40px; border-radius: 10px;
  background: rgba(144,12,39,0.1); display: flex; align-items: center; justify-content: center;
  color: #900C27; flex-shrink: 0; overflow: hidden;
}
.gl-preview-img-real { width: 100%; height: 100%; object-fit: cover; }
.gl-preview-info { flex: 1; min-width: 0; }
.gl-preview-name { font-size: 13.5px; font-weight: 700; color: #111318; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gl-preview-meta { display: flex; align-items: center; gap: 8px; margin-top: 2px; }
.gl-preview-slug { font-size: 11.5px; color: #9ca3af; }
.gl-preview-order { font-size: 11px; font-weight: 700; color: #900C27; background: rgba(144,12,39,0.1); padding: 2px 7px; border-radius: 20px; }
.gl-preview-status { display: flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 600; flex-shrink: 0; }
.gl-preview-status.active { color: #900C27; }
.gl-preview-status.inactive { color: #9ca3af; }
.gl-preview-dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }
.gl-preview-status.active .gl-preview-dot { background: #900C27; }
.gl-preview-status.inactive .gl-preview-dot { background: #9ca3af; }

/* ── Section ── */
.gl-section {
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px;
  overflow: hidden;
  animation: gl-section-in 0.3s ease both;
}
@keyframes gl-section-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
.gl-section-header {
  display: flex; align-items: center; gap: 12px;
  padding: 16px 20px; border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
}
.gl-section-icon {
  width: 32px; height: 32px; border-radius: 9px;
  background: rgba(144,12,39,0.09); color: #900C27;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.gl-section-title { font-size: 13.5px; font-weight: 700; color: #111318; }
.gl-section-desc { font-size: 12px; color: #9ca3af; margin-top: 1px; }

/* ── Fields ── */
.gl-fields { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
.gl-field-row { display: flex; gap: 14px; }
.gl-field { display: flex; flex-direction: column; gap: 5px; flex: 1; }
.gl-label { font-size: 12.5px; font-weight: 600; color: #374151; }
.gl-req { color: #ef4444; margin-left: 2px; }
.gl-optional { font-weight: 400; color: #9ca3af; font-size: 11.5px; }
.gl-input {
  width: 100%; height: 38px; padding: 0 12px;
  border: 1.5px solid #e5e7eb; border-radius: 9px;
  font-size: 13.5px; color: #111318; background: #fff;
  outline: none; transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
}
.gl-input:focus { border-color: #900C27; box-shadow: 0 0 0 3px rgba(144,12,39,0.08); }
.gl-input.gl-input-err { border-color: #ef4444; }
.gl-select { appearance: none; background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 7L11 1' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; padding-right: 32px; cursor: pointer; }
.gl-textarea { height: auto; padding: 10px 12px; resize: vertical; line-height: 1.6; }
.gl-hint { font-size: 11.5px; color: #9ca3af; margin-top: 1px; }
.gl-err { font-size: 12px; color: #ef4444; }

/* ── Image upload ── */
.gl-img-upload {
  border: 2px dashed #e5e7eb; border-radius: 12px;
  cursor: pointer; transition: all 0.15s; overflow: hidden;
  min-height: 140px; display: flex; align-items: center; justify-content: center;
}
.gl-img-upload:hover { border-color: #900C27; background: rgba(144,12,39,0.02); }
.gl-img-upload--has { border-style: solid; border-color: #e5e7eb; }
.gl-img-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 32px; }
.gl-img-icon {
  width: 48px; height: 48px; border-radius: 14px;
  background: rgba(144,12,39,0.08); color: #900C27;
  display: flex; align-items: center; justify-content: center;
}
.gl-img-label { font-size: 13.5px; font-weight: 600; color: #374151; }
.gl-img-sub { font-size: 12px; color: #9ca3af; }
.gl-img-uploading { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 32px; font-size: 13px; color: #9ca3af; }
.gl-img-preview { position: relative; width: 100%; }
.gl-img-thumb { width: 100%; height: 200px; object-fit: cover; display: block; }
.gl-img-overlay {
  position: absolute; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px; color: #fff; opacity: 0; transition: opacity 0.15s;
  font-size: 13px; font-weight: 600;
}
.gl-img-upload:hover .gl-img-overlay { opacity: 1; }

/* ── Toggle row ── */
.gl-toggle-row { display: flex; align-items: center; gap: 14px; }
.gl-toggle-wrap { flex-shrink: 0; cursor: pointer; }
.gl-toggle {
  width: 40px; height: 22px; border-radius: 11px;
  background: #e5e7eb; position: relative; transition: background 0.2s;
}
.gl-toggle.on { background: #900C27; }
.gl-toggle-knob {
  position: absolute; top: 3px; left: 3px;
  width: 16px; height: 16px; border-radius: 50%; background: #fff;
  transition: transform 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.15);
}
.gl-toggle.on .gl-toggle-knob { transform: translateX(18px); }
.gl-toggle-info { display: flex; flex-direction: column; gap: 2px; }
.gl-toggle-label { font-size: 13.5px; font-weight: 600; color: #111318; cursor: pointer; }
.gl-toggle-desc { font-size: 12px; color: #9ca3af; }

/* ── Form actions ── */
.gl-form-actions { display: flex; align-items: center; gap: 10px; padding: 4px 0; }
.gl-submit-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 10px 20px; border-radius: 10px; border: none;
  background: #900C27; color: #fff;
  font-size: 13.5px; font-weight: 600; cursor: pointer;
  transition: all 0.15s; box-shadow: 0 2px 8px rgba(144,12,39,0.25);
}
.gl-submit-btn:hover:not(:disabled) { background: #6f0a1e; box-shadow: 0 4px 14px rgba(144,12,39,0.35); }
.gl-submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.gl-cancel-btn {
  display: inline-flex; align-items: center;
  padding: 10px 18px; border-radius: 10px;
  border: 1.5px solid #e5e7eb; background: #fff;
  color: #6b7280; font-size: 13.5px; font-weight: 600;
  text-decoration: none; cursor: pointer; transition: all 0.15s;
}
.gl-cancel-btn:hover { border-color: #d1d5db; background: #f9fafb; color: #374151; }

/* ── Right: Preview card ── */
.gl-cat-preview-col { display: flex; flex-direction: column; gap: 16px; position: sticky; top: 20px; }

.gl-cat-preview-card {
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px;
  overflow: hidden; animation: gl-section-in 0.3s ease 0.1s both;
}
.gl-cat-preview-label {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.8px; color: #9ca3af;
  padding: 14px 16px 0;
}
.gl-cat-preview-img-wrap { position: relative; height: 130px; margin: 12px 14px 0; border-radius: 10px; overflow: hidden; }
.gl-cat-preview-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.gl-cat-preview-img-placeholder {
  width: 100%; height: 100%;
  background: linear-gradient(135deg, #f0fdf4 0%, #d1fae5 100%);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px; color: #6ee7b7; font-size: 11.5px; color: #9ca3af;
}
.gl-cat-preview-order {
  position: absolute; top: 8px; left: 8px;
  background: rgba(0,0,0,0.65); color: #fff;
  font-size: 10.5px; font-weight: 700; padding: 3px 8px; border-radius: 20px;
}
.gl-cat-preview-info { padding: 12px 16px 0; }
.gl-cat-preview-name { font-size: 14px; font-weight: 700; color: #111318; }
.gl-cat-preview-slug { font-size: 11.5px; color: #9ca3af; margin-top: 1px; }
.gl-cat-preview-meta {
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 10px;
}
.gl-cat-preview-count { display: flex; align-items: center; gap: 4px; font-size: 12px; color: #6b7280; }
.gl-cat-preview-badge {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11.5px; font-weight: 600; padding: 3px 9px; border-radius: 20px;
}
.gl-cat-preview-badge.active { background: rgba(144,12,39,0.1); color: #900C27; }
.gl-cat-preview-badge.inactive { background: #f3f4f6; color: #9ca3af; }
.gl-cat-preview-dot { width: 5px; height: 5px; border-radius: 50%; display: inline-block; }
.gl-cat-preview-badge.active .gl-cat-preview-dot { background: #900C27; }
.gl-cat-preview-badge.inactive .gl-cat-preview-dot { background: #9ca3af; }

.gl-cat-preview-actions {
  display: flex; gap: 6px; padding: 12px 14px 14px;
  border-top: 1px solid #f3f4f6; margin-top: 12px;
}
.gl-cat-preview-btn-edit {
  flex: 1; height: 30px; border-radius: 7px;
  background: rgba(144,12,39,0.08); color: #900C27;
  display: flex; align-items: center; justify-content: center; gap: 5px;
  font-size: 12px; font-weight: 600; opacity: 0.6;
}
.gl-cat-preview-btn-del {
  flex: 0 0 30px; width: 30px; height: 30px; border-radius: 7px;
  background: rgba(239,68,68,0.07); color: #ef4444;
  display: flex; align-items: center; justify-content: center; opacity: 0.6;
}

/* ── Tips card ── */
.gl-tips-card {
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px;
  padding: 16px 18px;
  animation: gl-section-in 0.3s ease 0.2s both;
}
.gl-tips-title {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 700; color: #374151; margin-bottom: 10px;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.gl-tips-title svg { color: #900C27; }
.gl-tips-list {
  margin: 0; padding-left: 16px;
  display: flex; flex-direction: column; gap: 7px;
}
.gl-tips-list li { font-size: 12px; color: #6b7280; line-height: 1.5; }

/* ── Sticky bar (hidden on desktop) ── */
.gl-sticky-bar {
  display: none;
  position: fixed; bottom: 0; left: 0; right: 0; z-index: 100;
  background: #fff; border-top: 1.5px solid #f0f1f3;
  padding: 12px 16px; gap: 10px;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.06);
}
.gl-sticky-cancel { display: flex; align-items: center; justify-content: center; }
.gl-sticky-submit { flex: 1; justify-content: center; }

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  .gl-form-page { padding-bottom: 88px; }
  .gl-form-header { padding: 14px 0 12px; }
  .gl-form-title { font-size: 18px !important; }

  /* Stack two-column layout, hide sidebar */
  .gl-cat-layout { grid-template-columns: 1fr !important; gap: 12px; }
  .gl-cat-preview-col { display: none !important; }

  /* Stack name + sort order */
  .gl-field-row { flex-direction: column !important; gap: 12px !important; }
  .gl-field[style*="flex:3"],
  .gl-field[style*="flex:1"] { flex: 1 !important; }

  /* Bigger tap targets */
  .gl-input { height: 46px !important; font-size: 15px !important; }
  .gl-textarea { height: auto !important; }

  /* Section padding */
  .gl-fields { padding: 16px !important; gap: 14px !important; }

  /* Hide desktop actions, show sticky */
  .gl-form-actions { display: none !important; }
  .gl-sticky-bar { display: flex !important; }
}
</style>

<style>
/* ── Dark mode ── */
body.dark-only .gl-form-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-form-breadcrumb,
body.dark-only .gl-form-breadcrumb span { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-form-breadcrumb a { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-form-breadcrumb a:hover { color: #900C27 !important; }

body.dark-only .gl-section { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-section-header { background: transparent !important; border-bottom-color: transparent !important; }
body.dark-only .gl-section-title { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-section-desc { color: rgba(255,255,255,0.3) !important; }

body.dark-only .gl-label { color: rgba(255,255,255,0.7) !important; }
body.dark-only .gl-input { background: #1d1e26 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-input:focus { border-color: #900C27 !important; box-shadow: 0 0 0 3px rgba(144,12,39,0.15) !important; }
body.dark-only .gl-hint { color: rgba(255,255,255,0.25) !important; }
body.dark-only .gl-optional { color: rgba(255,255,255,0.25) !important; }

body.dark-only .gl-img-upload { border-color: #3d4255 !important; }
body.dark-only .gl-img-upload:hover { border-color: #900C27 !important; background: rgba(144,12,39,0.06) !important; }
body.dark-only .gl-img-label { color: rgba(255,255,255,0.7) !important; }
body.dark-only .gl-img-sub { color: rgba(255,255,255,0.25) !important; }

body.dark-only .gl-toggle { background: #3d4255 !important; }
body.dark-only .gl-toggle.on { background: #900C27 !important; }
body.dark-only .gl-toggle-label { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-toggle-desc { color: rgba(255,255,255,0.3) !important; }

body.dark-only .gl-cancel-btn { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.6) !important; }

body.dark-only .gl-preview-strip { background: rgba(144,12,39,0.12) !important; border-color: rgba(144,12,39,0.2) !important; }
body.dark-only .gl-preview-name { color: rgba(255,255,255,0.87) !important; }

body.dark-only .gl-cat-preview-card { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-cat-preview-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-cat-preview-slug { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-cat-preview-count { color: rgba(255,255,255,0.4) !important; }
body.dark-only .gl-cat-preview-actions { border-top-color: #323644 !important; }

body.dark-only .gl-tips-card { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-tips-title { color: rgba(255,255,255,0.6) !important; }
body.dark-only .gl-tips-list li { color: rgba(255,255,255,0.4) !important; }

body.dark-only .gl-sticky-bar { background: #1d1e26 !important; border-top-color: #323644 !important; }
</style>
